import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { QrCode, Copy, CheckCircle, Wallet, ClockClockwise, CurrencyCircleDollar } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function ResellerBilling() {
  const { user } = useAuth();
  const brand = user?.reseller_brand_color || "#0F4C3A";
  const [balance, setBalance] = useState(null);
  const [earningsBalance, setEarningsBalance] = useState(null);
  const [tx, setTx] = useState([]);
  const [earnings, setEarnings] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [config, setConfig] = useState({ credit_price_brl: 15, min_credits: 5, configured: true });
  const [credits, setCredits] = useState(5);
  const [cpf, setCpf] = useState("");
  const [checkout, setCheckout] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [pixKey, setPixKey] = useState("");
  const [wAmount, setWAmount] = useState("");

  const load = async () => {
    try {
      const [m, t, cfg, e, w] = await Promise.all([
        api.get("/reseller/metrics"),
        api.get("/reseller/transactions"),
        api.get("/reseller/pix/config"),
        api.get("/reseller/earnings"),
        api.get("/reseller/withdrawals"),
      ]);
      setBalance(m.data.credit_balance);
      setEarningsBalance(m.data.earnings_balance);
      setTx(t.data);
      setConfig(cfg.data);
      setCredits(cfg.data.min_credits);
      setEarnings(e.data);
      setWithdrawals(w.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const requestWithdrawal = async () => {
    if (!pixKey.trim()) return toast.error("Informe sua chave Pix");
    if (Number(wAmount) <= 0) return toast.error("Valor inválido");
    try {
      await api.post("/reseller/withdrawals", { amount_brl: Number(wAmount), pix_key: pixKey.trim() });
      toast.success("Solicitação de saque enviada. Aguarde o processamento.");
      setWithdrawOpen(false); setPixKey(""); setWAmount("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Poll payment status while checkout is pending
  useEffect(() => {
    if (!checkout?.purchase_id || checkout.status === "approved") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/reseller/pix/purchases/${checkout.purchase_id}`);
        setCheckout((prev) => ({ ...prev, status: data.status }));
        if (data.status === "approved") {
          toast.success("Pagamento confirmado! Créditos adicionados.");
          load();
          clearInterval(t);
        }
      } catch { /* ignore */ }
    }, 5000);
    return () => clearInterval(t);
  }, [checkout?.purchase_id, checkout?.status]);

  const startCheckout = async () => {
    if (!cpf.trim()) return toast.error("Informe seu CPF para o Pix");
    if (Number(credits) < config.min_credits) return toast.error(`Mínimo ${config.min_credits} créditos`);
    setLoading(true);
    try {
      const { data } = await api.post("/reseller/pix/checkout", {
        credits: Number(credits),
        payer_cpf: cpf.replace(/\D/g, ""),
      });
      setCheckout(data);
      toast.success("QR Code Pix gerado. Escaneie no seu banco.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(checkout.qr_code || "");
    setCopied(true);
    toast.success("Código Pix copiado");
    setTimeout(() => setCopied(false), 2000);
  };

  const amount = (Number(credits) * config.credit_price_brl).toFixed(2);

  return (
    <div className="p-8">
      <div className="mb-8">
        <p className="text-xs font-semibold tracking-[0.3em] uppercase" style={{ color: brand }}>Financeiro</p>
        <h1 className="text-4xl font-black tracking-tighter flex items-center gap-3">
          <Wallet size={36} weight="duotone" style={{ color: brand }} />
          Créditos
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Balance */}
        <div className="bg-white border border-[#E5E5E0] shadow-brutal p-6">
          <p className="text-[10px] uppercase tracking-widest font-bold text-[#5C5C59]">Saldo atual</p>
          <p className="text-6xl font-black tracking-tighter mt-3 font-mono" style={{ color: balance < 5 ? "#DC2626" : brand }} data-testid="billing-balance">
            {balance ?? "—"}
          </p>
          <p className="text-xs text-[#5C5C59] mt-2">Cada empresa criada consome 1 crédito (ou mais, se plano próprio).</p>
        </div>

        {/* Purchase */}
        <div className="bg-white border border-[#E5E5E0] shadow-brutal p-6">
          <p className="text-[10px] uppercase tracking-widest font-bold text-[#5C5C59]">Comprar créditos via Pix</p>
          <div className="mt-4 space-y-3">
            <div>
              <Label className="text-xs uppercase font-semibold tracking-wider">Quantidade (mínimo {config.min_credits})</Label>
              <Input type="number" min={config.min_credits} data-testid="credits-input" value={credits} onChange={(e) => setCredits(e.target.value)} className="rounded-none mt-1 font-mono text-lg" />
              <p className="text-xs text-[#5C5C59] mt-1">Total: <b>R$ {amount}</b> ({config.credit_price_brl.toFixed(2)} BRL por crédito)</p>
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold tracking-wider">CPF do pagador</Label>
              <Input data-testid="cpf-input" value={cpf} onChange={(e) => setCpf(e.target.value)} className="rounded-none mt-1 font-mono" placeholder="000.000.000-00" />
            </div>
            <Button onClick={startCheckout} disabled={loading} data-testid="start-pix-btn" className="rounded-none w-full text-white" style={{ background: brand }}>
              {loading ? "Gerando…" : "Gerar Pix"}
            </Button>
          </div>
        </div>
      </div>

      {/* Pix result */}
      {checkout && (
        <div className="bg-white border-2 shadow-brutal p-6 mb-8" style={{ borderColor: brand }} data-testid="pix-result">
          {checkout.status === "approved" ? (
            <div className="text-center py-6">
              <CheckCircle size={48} weight="fill" style={{ color: brand }} className="mx-auto" />
              <p className="text-2xl font-black mt-3">Pagamento confirmado!</p>
              <p className="text-sm text-[#5C5C59] mt-1">{checkout.credits} créditos adicionados à sua conta.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-[10px] uppercase tracking-widest font-bold text-[#5C5C59]">Escaneie no seu banco</p>
                {checkout.qr_code_base64 ? (
                  <img src={`data:image/png;base64,${checkout.qr_code_base64}`} alt="Pix QR" className="w-full max-w-[240px] border border-[#E5E5E0] mt-2" />
                ) : (
                  <div className="w-full max-w-[240px] aspect-square bg-[#F7F7F5] border border-[#E5E5E0] mt-2 flex items-center justify-center">
                    <QrCode size={48} className="text-[#5C5C59] opacity-50" />
                  </div>
                )}
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-[10px] uppercase tracking-widest font-bold text-[#5C5C59]">Ou copie o código Pix</p>
                  <div className="mt-2 p-3 bg-[#F7F7F5] border border-[#E5E5E0] font-mono text-[11px] break-all max-h-32 overflow-y-auto" data-testid="pix-copy-code">
                    {checkout.qr_code || "—"}
                  </div>
                  <Button onClick={copyCode} className="rounded-none w-full mt-2 text-white" style={{ background: brand }} data-testid="pix-copy-btn">
                    <Copy size={14} weight="bold" className="mr-2" />
                    {copied ? "Copiado!" : "Copiar código"}
                  </Button>
                </div>
                <div className="p-3 bg-[#FEF3C7] border-l-[3px] border-[#D97706]">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-[#92400E] flex items-center gap-1">
                    <ClockClockwise size={12} weight="bold" /> Aguardando pagamento
                  </p>
                  <p className="text-xs mt-1">Após pagar, os créditos entram automaticamente. Status atualiza a cada 5s.</p>
                  <p className="text-xs mt-1">Status: <b className="font-mono">{checkout.status}</b></p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Transactions */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white border-2 shadow-brutal p-6" style={{ borderColor: brand }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-widest font-bold text-[#5C5C59] flex items-center gap-1"><CurrencyCircleDollar size={14} /> Ganhos disponíveis</p>
              <p className="text-4xl font-black tracking-tighter mt-2 font-mono" style={{ color: brand }} data-testid="billing-earnings">R$ {(earningsBalance ?? 0).toFixed(2)}</p>
              <p className="text-xs text-[#5C5C59] mt-1">Comissões recebidas pelas vendas do link público.</p>
            </div>
            <Button onClick={() => setWithdrawOpen(true)} disabled={!earningsBalance || earningsBalance <= 0} data-testid="withdraw-btn" className="rounded-none text-white" style={{ background: brand }}>
              Sacar via Pix
            </Button>
          </div>
        </div>
        <div className="bg-white border border-[#E5E5E0] shadow-brutal p-6">
          <p className="text-[10px] uppercase tracking-widest font-bold text-[#5C5C59]">Últimas comissões</p>
          <ul className="text-xs mt-2 space-y-1 max-h-32 overflow-y-auto" data-testid="earnings-list">
            {earnings.length === 0 && <li className="text-[#5C5C59]">Ainda sem comissões</li>}
            {earnings.slice(0, 6).map((e) => (
              <li key={e.id} className="flex justify-between">
                <span className="truncate mr-2">{e.description}</span>
                <b className="font-mono whitespace-nowrap" style={{ color: e.amount_brl >= 0 ? brand : "#DC2626" }}>
                  {e.amount_brl >= 0 ? "+" : ""}R$ {e.amount_brl.toFixed(2)}
                </b>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {withdrawals.length > 0 && (
        <div className="bg-white border border-[#E5E5E0] shadow-brutal mb-8">
          <div className="p-4 border-b border-[#E5E5E0]">
            <p className="text-[10px] uppercase tracking-widest font-bold text-[#5C5C59]">Solicitações de saque</p>
          </div>
          <table className="w-full text-sm" data-testid="withdrawals-table">
            <thead>
              <tr className="border-b border-[#E5E5E0] text-[10px] uppercase tracking-wider text-[#5C5C59]">
                <th className="text-left px-4 py-2">Data</th>
                <th className="text-left px-4 py-2">Chave Pix</th>
                <th className="text-center px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Valor</th>
              </tr>
            </thead>
            <tbody>
              {withdrawals.map((w) => (
                <tr key={w.id} className="border-b border-[#F0F0EC]">
                  <td className="px-4 py-2 text-xs text-[#5C5C59]">{new Date(w.created_at).toLocaleString("pt-BR")}</td>
                  <td className="px-4 py-2 font-mono text-xs">{w.pix_key}</td>
                  <td className="px-4 py-2 text-center">
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 ${w.status === "paid" ? "bg-[#EDF3F0] text-[#0F4C3A]" : w.status === "pending" ? "bg-[#FEF3C7] text-[#92400E]" : "bg-[#FEE2E2] text-[#DC2626]"}`}>{w.status}</span>
                  </td>
                  <td className="px-4 py-2 text-right font-mono font-bold">R$ {w.amount_brl.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={withdrawOpen} onOpenChange={setWithdrawOpen}>
        <DialogContent className="bg-white rounded-none border-[#E5E5E0] max-w-md" data-testid="withdraw-dialog">
          <DialogHeader><DialogTitle>Solicitar saque via Pix</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="p-3 bg-[#F7F7F5] border-l-[3px]" style={{ borderColor: brand }}>
              <div className="text-[10px] uppercase tracking-wider text-[#5C5C59]">Saldo disponível</div>
              <div className="text-2xl font-black font-mono" style={{ color: brand }}>R$ {(earningsBalance ?? 0).toFixed(2)}</div>
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold tracking-wider">Valor (R$)</Label>
              <Input type="number" data-testid="withdraw-amount-input" value={wAmount} onChange={(e) => setWAmount(e.target.value)} className="rounded-none mt-1 font-mono" placeholder="0,00" />
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold tracking-wider">Chave Pix (CPF/CNPJ/email/telefone)</Label>
              <Input data-testid="withdraw-pix-input" value={pixKey} onChange={(e) => setPixKey(e.target.value)} className="rounded-none mt-1 font-mono" placeholder="Sua chave Pix" />
            </div>
            <p className="text-xs text-[#5C5C59]">O saque é processado manualmente pelo administrador. Você receberá o Pix em até 1 dia útil.</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setWithdrawOpen(false)} className="rounded-none">Cancelar</Button>
            <Button data-testid="submit-withdraw" onClick={requestWithdrawal} className="rounded-none text-white" style={{ background: brand }}>Solicitar saque</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transactions (credits) */}
      <div className="bg-white border border-[#E5E5E0] shadow-brutal">
        <div className="p-4 border-b border-[#E5E5E0]">
          <p className="text-[10px] uppercase tracking-widest font-bold text-[#5C5C59]">Extrato de créditos</p>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E5E5E0] text-[10px] uppercase tracking-wider text-[#5C5C59]">
              <th className="text-left px-4 py-3">Data</th>
              <th className="text-left px-4 py-3">Tipo</th>
              <th className="text-left px-4 py-3">Descrição</th>
              <th className="text-right px-4 py-3">Créditos</th>
            </tr>
          </thead>
          <tbody>
            {tx.length === 0 && (
              <tr><td colSpan={4} className="text-center py-8 text-[#5C5C59]">Nenhuma transação ainda.</td></tr>
            )}
            {tx.map((t) => (
              <tr key={t.id} className="border-b border-[#F0F0EC]">
                <td className="px-4 py-3 text-xs text-[#5C5C59]">{new Date(t.created_at).toLocaleString("pt-BR")}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 ${
                    t.type === "purchase" ? "bg-[#EDF3F0] text-[#0F4C3A]" :
                    t.type === "spend" ? "bg-[#FEE2E2] text-[#DC2626]" :
                    "bg-[#F7F7F5] text-[#5C5C59]"
                  }`}>{t.type}</span>
                </td>
                <td className="px-4 py-3 text-xs">{t.description || "—"}</td>
                <td className="px-4 py-3 text-right font-mono font-bold" style={{ color: t.amount >= 0 ? brand : "#DC2626" }}>
                  {t.amount > 0 ? "+" : ""}{t.amount}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
